/**
 * GalleryPage
 * Grid-Übersicht aller Kategorien
 *
 * Mobile: Zeigt statische CSS-Shapes (kein WebGL um iOS Safari Crash zu vermeiden)
 * Desktop: Zeigt 3D Previews mit rotierenden Skulpturen
 */

import { Suspense, useMemo, useRef, useState, useEffect, Component, ErrorInfo, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Canvas, useFrame } from '@react-three/fiber'
import { useGLTF } from '@react-three/drei'
import { EffectComposer, Bloom } from '@react-three/postprocessing'
import { Box3, Vector3, Group } from 'three'
import { useArtworks } from '@/hooks/useArtworks'
import { useTheme } from '@/hooks/useTheme'
import { ShapeType } from '@/components/three/GlowShape'
import { Artwork } from '@/types/artwork'
import { mediaUrl } from '@/utils/mediaUrl'
import { Seo } from '@/components/Seo'
import { useT, STRINGS, useFieldLang, useCategoryTitle } from '@/context/LanguageContext'
import './GalleryPage.css'

// Verschiedene Formen für Kategorien
const CATEGORY_SHAPES: ShapeType[] = ['ring', 'triangle', 'square', 'pentagon', 'hexagon']

/**
 * Detect mobile device
 */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const check = () => {
      const mobile = window.matchMedia('(max-width: 768px)').matches ||
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0
      setIsMobile(mobile)
    }
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  return isMobile
}

/**
 * Error Boundary for WebGL crashes
 */
class Canvas3DErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('3D Canvas Error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback
    }
    return this.props.children
  }
}

/**
 * Rotating sculpture wrapper
 */
function RotatingSculpture({ children, speed = 0.5 }: { children: React.ReactNode; speed?: number }) {
  const groupRef = useRef<Group>(null)

  useFrame((_, delta) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += delta * speed
    }
  })

  return <group ref={groupRef}>{children}</group>
}

/**
 * Mini sculpture from GLB
 */
function MiniSculpture({ url }: { url: string }) {
  const { scene: originalScene } = useGLTF(url)

  const { clonedScene, scale, offset } = useMemo(() => {
    const cloned = originalScene.clone(true)
    cloned.position.set(0, 0, 0)
    cloned.rotation.set(0, 0, 0)
    cloned.scale.set(1, 1, 1)
    cloned.updateMatrixWorld(true)

    const box = new Box3().setFromObject(cloned)
    const size = box.getSize(new Vector3())
    const center = box.getCenter(new Vector3())

    const targetHeight = 1.8
    const maxDim = Math.max(size.x, size.y, size.z)
    const s = maxDim > 0 ? targetHeight / maxDim : 1

    const off = new Vector3(-center.x * s, -center.y * s - 0.1, -center.z * s)
    return { clonedScene: cloned, scale: s, offset: off }
  }, [originalScene, url])

  return (
    <group position={offset}>
      <primitive object={clonedScene} scale={scale} />
    </group>
  )
}

/**
 * Get thumbnail URL for artwork based on theme
 * Thumbnails are pre-rendered PNGs in /content/thumbnails/{theme}/
 */
function getThumbnailUrl(artwork: Artwork | undefined): string | null {
  if (!artwork?.slug) return null
  // Pre-rendered PNGs exist for both GLB and PLY-only artworks.
  if (!artwork.files?.glb && !artwork.files?.ply) return null
  return `/content/thumbnails/dark/${artwork.slug}.png`
}

/**
 * Get preview image URL for any artwork
 * Priority: thumbnail field > image > generated hero URL
 */
function getPreviewImageUrl(artwork?: Artwork): string | null {
  if (!artwork) return null

  if (artwork.files?.thumbnail) return mediaUrl(artwork.files.thumbnail)
  if (artwork.files?.image) return mediaUrl(artwork.files.image)
  return null
}

/**
 * Static preview image with error handling
 */
function ImagePreview({
  imageUrl,
  alt,
  onError
}: {
  imageUrl: string
  alt?: string
  onError: () => void
}) {
  return (
    <div className="hero-image-preview">
      <img
        src={imageUrl}
        alt={alt || 'Skulptur Vorschau'}
        loading="lazy"
        onError={onError}
      />
    </div>
  )
}

/**
 * Video preview for mobile - autoplay looping video
 */
function VideoPreview({
  videoUrl,
  onError
}: {
  videoUrl: string
  onError: () => void
}) {
  return (
    <div className="hero-video-preview">
      <video
        src={videoUrl}
        autoPlay
        loop
        muted
        playsInline
        onError={onError}
      />
    </div>
  )
}

/**
 * Get video URL for artwork based on theme
 */
function getVideoUrl(artwork: Artwork | undefined): string | null {
  if (!artwork?.slug) return null
  if (!artwork.files?.glb) return null  // Only GLB artworks have videos
  return `/content/videos/dark/${artwork.slug}.webm`
}

/**
 * Static CSS shape preview (mobile fallback)
 */
function StaticShapePreview({ shape }: { shape: ShapeType }) {
  const shapeClass = `mobile-shape mobile-shape-${shape}`
  return (
    <div className="mobile-preview">
      <div className={shapeClass} />
    </div>
  )
}

/**
 * Desktop 3D Preview with WebGL
 */
function Desktop3DPreview({
  shape,
  glbUrl,
  canvasBg
}: {
  shape: ShapeType
  glbUrl: string | null
  canvasBg: string
}) {
  return (
    <Canvas3DErrorBoundary fallback={<StaticShapePreview shape={shape} />}>
      <Canvas
        style={{ width: '100%', height: '100%', background: canvasBg }}
        gl={{ antialias: true, alpha: true, powerPreference: 'low-power' }}
        camera={{ position: [0, 0.2, 3], fov: 45 }}
      >
        <ambientLight intensity={0.7} />
        <directionalLight position={[5, 5, 5]} intensity={1.2} />

        <Suspense fallback={null}>
          {/* GlowShape removed — clean museum aesthetic */}
          {glbUrl && (
            <RotatingSculpture speed={0.5}>
              <MiniSculpture url={glbUrl} />
            </RotatingSculpture>
          )}
        </Suspense>

        <EffectComposer>
          <Bloom
            intensity={2.0}
            luminanceThreshold={0}
            luminanceSmoothing={0.4}
            mipmapBlur={true}
            radius={0.8}
          />
        </EffectComposer>
      </Canvas>
    </Canvas3DErrorBoundary>
  )
}

/**
 * Mini Glow Preview - Shows thumbnail image (preferred) or video,
 * falls back to 3D only if no static assets exist.
 */
function MiniGlowPreview({
  index,
  artwork,
  canvasBg
}: {
  index: number
  artwork?: Artwork
  canvasBg: string
}) {
  const shape = CATEGORY_SHAPES[index % CATEGORY_SHAPES.length]
  const glbUrl = mediaUrl(artwork?.files?.glb)
  const thumbnailUrl = getThumbnailUrl(artwork)
  const videoUrl = getVideoUrl(artwork)
  const imageUrl = getPreviewImageUrl(artwork)
  const isMobile = useIsMobile()
  const [thumbnailError, setThumbnailError] = useState(false)
  const [videoError, setVideoError] = useState(false)
  const [imageError, setImageError] = useState(false)

  const previewAlt = artwork?.title
    ? `Skulptur ${artwork.title} von Anna Sacher Santana`
    : undefined

  // Try thumbnail first (both mobile and desktop)
  if (thumbnailUrl && !thumbnailError) {
    return (
      <ImagePreview
        imageUrl={thumbnailUrl}
        alt={previewAlt}
        onError={() => setThumbnailError(true)}
      />
    )
  }

  // Try video (mobile preference, desktop fallback)
  if (videoUrl && !videoError) {
    return (
      <VideoPreview
        videoUrl={videoUrl}
        onError={() => setVideoError(true)}
      />
    )
  }

  // Try image file
  if (imageUrl && !imageError) {
    return (
      <ImagePreview
        imageUrl={imageUrl}
        alt={previewAlt}
        onError={() => setImageError(true)}
      />
    )
  }

  // MOBILE: CSS shape fallback
  if (isMobile) {
    return <StaticShapePreview shape={shape} />
  }

  // DESKTOP: 3D fallback (only if no thumbnails/videos exist)
  return (
    <Desktop3DPreview shape={shape} glbUrl={glbUrl} canvasBg={canvasBg} />
  )
}

/**
 * GalleryPage Component
 */
export function GalleryPage() {
  const { categories, loading, getArtworksByCategory } = useArtworks()
  const { canvasBg } = useTheme()
  const t = useT()
  const pick = useFieldLang()
  const tCat = useCategoryTitle()

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>{t(STRINGS.common.loading)}</p>
      </div>
    )
  }

  // Filter categories with artworks
  const activeCategories = categories.filter(c => c.artworkCount > 0)

  // Get first artwork with 3D file for each category
  const getPreviewArtwork = (categoryId: string): Artwork | undefined => {
    const artworks = getArtworksByCategory(categoryId)
    // Prefer GLB (has a pre-rendered thumbnail), then PLY, then any image-only
    // artwork so photo-only categories like "Out-door Impressionen" don't fall
    // through to the CSS-shape placeholder.
    return (
      artworks.find(a => a.files?.glb) ||
      artworks.find(a => a.files?.ply) ||
      artworks.find(a => a.files?.image)
    )
  }

  return (
    <main className="gallery-page">
      <Seo
        title={t(STRINGS.sections.gallery)}
        description={t(STRINGS.sections.gallerySeoDescription)}
      />
      <div className="gallery-container">
        <header className="gallery-header">
          <h1>{t(STRINGS.sections.gallery)}</h1>
          <p>{t(STRINGS.sections.galleryTagline)}</p>
        </header>

        <div className="category-grid">
          {activeCategories.map((category, index) => {
            const previewArtwork = getPreviewArtwork(category.id)
            return (
              <Link
                key={category.id}
                to={`/galerie/${category.slug}`}
                className="category-card"
              >
                <div className="category-image">
                  <div className="category-preview">
                    <MiniGlowPreview
                      index={index}
                      artwork={previewArtwork}
                      canvasBg={canvasBg}
                    />
                    <span className="category-count-overlay">
                      {category.artworkCount}
                    </span>
                  </div>
                </div>

                <div className="category-info">
                  <h2 className="category-title">{tCat(category.title)}</h2>
                  {(() => {
                    const localDesc = pick(category.description, category.description_en) || category.description
                    return localDesc ? (
                      <p className="category-description">
                        {localDesc.substring(0, 120)}
                        {localDesc.length > 120 ? '...' : ''}
                      </p>
                    ) : null
                  })()}
                  <span className="category-link-text">
                    {category.artworkCount} {t(STRINGS.sections.viewWorks)} →
                  </span>
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    </main>
  )
}
